import React, { useState } from 'react';
import { Button, Input, Field, Badge } from '@fluentui/react-components';
import { usePersons, useDeletePerson, Person } from '#root/queries/persons';
import LoadingWrapper from '#root/components/LoadingWrapper';
import { Edit24Filled, Delete24Filled, ShieldPersonRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import Paginator from '#root/components/Paginator';
import { showToast } from '#root/components/GlobalToaster';
import './styles.scss';
import InfiniteDropdown from '#root/components/InfiniteDropdown';
import { useInfiniteIncidents, useIncidentById, Incident } from '#root/queries/incidents';
import { useInfiniteDropdown } from '#root/hooks/useInfiniteDropdown';
import { PERSON_TYPES } from '#root/utils/persons';
import { ROUTES } from '#root/routes/constants';
import { useCurrentUser, useVTrackerSelectedIncident } from '#root/queries/local';
import { DialogBox } from '#root/components/DialogBox';
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { PersonCard } from '#root/components/PersonCardContent';

const AllVictims: React.FC = () => {
    const { data: currentUser } = useCurrentUser();
    const unit = currentUser?.unit;
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [personToDelete, setPersonToDelete] = useState<Person | null>(null);
    const { selectedIncidentId: vtrackerIncidentId, setVTrackerSelectedIncidentId } = useVTrackerSelectedIncident();
    const [selectedIncidentId, setSelectedIncidentId] = useState<string | ''>(vtrackerIncidentId || '');
    const [nameFilter, setNameFilter] = useState<string>('');

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult
    } = useInfiniteDropdown<Incident>({
        initialId: selectedIncidentId || '',
        onChange: (id) => {
            setSelectedIncidentId(id || '');
            if (id) {
                setVTrackerSelectedIncidentId(id);
            }
            setCurrentPage(1);
        },
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    const {
        data: personsResponse,
        isLoading,
        refetch
    } = usePersons({
        incidentId: selectedIncidentId || '',
        name: nameFilter,
        type: PERSON_TYPES.VICTIM,
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage
    });

    const deleteMutation = useDeletePerson();

    const totalCount = personsResponse?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const persons = personsResponse?.items ?? [];

    const handleEdit = (person: Person) => {
        navigate(`${ROUTES.VTRACKER.EDIT_VICTIM}?personId=${person.id}`);
    };

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        refetch();
    };

    // Delete dialog handling
    const handleDeleteClick = (person: Person, e: React.MouseEvent) => {
        e.stopPropagation();
        setPersonToDelete(person);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (personToDelete?.id) {
            try {
                await deleteMutation.mutateAsync({ id: personToDelete.id });
                showToast('Victim deleted successfully', 'success');
                setIsDeleteDialogOpen(false);
                refetch();
            } catch (error) {
                showToast('Failed to delete victim', 'error');
            }
        }
    };

    // Rendering
    const renderVictimCard = (person: Person) => {
        return (
            <div key={person.id} className="large-card victim-card">
                <div className="large-card__content">
                    <PersonCard
                        person={person}
                        unit={unit}
                    />
                </div>
                <div className="large-card__actions">
                    <Button
                        icon={<Edit24Filled />}
                        appearance="subtle"
                        onClick={() => handleEdit(person)}
                    />
                    <Button
                        icon={<Delete24Filled />}
                        appearance="subtle"
                        onClick={(e) => handleDeleteClick(person, e)}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="all-victims">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                    <div className="emc-page-header__props">
                        <Field label="Incident" className="all-victims__incident">
                            <InfiniteDropdown
                                value={selectedIncidentId || ''}
                                onChange={(_, data) => handleIncidentChange(data.value)}
                                onFilterChange={setIncidentFilter}
                                queryResult={incidentQueryResult}
                                itemToOption={(incident) => ({
                                    value: incident.id ?? '',
                                    text: incident.name ?? ''
                                })}
                                placeholder="All Incidents"
                                selectedItem={selectedIncidentData}
                                clearable
                            />
                        </Field>
                        <Field label="Name" className="all-victims__name-filter">
                            <Input
                                placeholder="Filter by Name"
                                value={nameFilter}
                                onChange={(_, data) => {
                                    setNameFilter(data.value);
                                    setCurrentPage(1);
                                    refetch();
                                }}
                            />
                        </Field>
                        <Badge size="extra-large" color="success" className="all-victims__count">
                            Victims: {totalCount}
                        </Badge>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>All Victims <div className="divider" /></h2>
                </div>
            </div>

            <div className="all-victims__content">
                <LoadingWrapper
                    isLoading={isLoading}
                    isEmpty={persons.length === 0}
                    emptyMessage="No victims found"
                    emptyIcon={<ShieldPersonRegular />}
                >
                    <div>
                        <div className="all-victims__list">
                            {persons.map((person) => renderVictimCard(person))}
                            <Paginator
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                totalItems={totalCount}
                                currentItems={(currentPage - 1) * itemsPerPage + 1}
                                showItemsPerPage={true}
                            />
                        </div>
                    </div>
                </LoadingWrapper>
            </div>

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title="Delete Victim"
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={deleteMutation.isPending}
            >
                {personToDelete && (
                    <>Are you sure you want to delete {personToDelete?.fullName}?</>
                )}
            </DialogBox>
        </div>
    );
};

export default AllVictims;
